/**
 * Enhanced Product Gallery Slider for Dawn Theme
 * Integrates with Dawn's native media-gallery and slider-component functionality
 * Features:
 * - Uses Dawn's native setActiveMedia method
 * - Variant image switching when variants are selected
 * - Click handlers for variant option images
 * - Works seamlessly with Dawn's existing slider functionality
 */

if (!customElements.get('product-gallery-slider')) {
  customElements.define(
    'product-gallery-slider',
    class ProductGallerySlider extends HTMLElement {
      constructor() {
        super();
        this.mediaGallery = null;
        this.sectionId = null;
        this.variantMediaMap = new Map();
        // Don't create a shadow DOM - let Dawn's native components work
        this.init();
      }

      init() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.setup());
        } else {
          this.setup();
        }
      }

      setup() {
        // Find Dawn's native media-gallery component
        this.mediaGallery = this.querySelector('media-gallery') || 
                           this.closest('.product__media-wrapper')?.querySelector('media-gallery') ||
                           document.querySelector('media-gallery');
        
        if (!this.mediaGallery) {
          console.warn('ProductGallerySlider: media-gallery not found');
          return;
        }

        // Get section ID from product-info
        const productInfo = document.querySelector('product-info');
        this.sectionId = productInfo?.dataset?.section;

        // Ensure desktop slider functionality works
        this.ensureDesktopSlider();

        // Build variant media map
        this.buildVariantMediaMap();

        // Listen to variant changes (Dawn's native events)
        this.setupVariantListeners();

        // Listen to variant option image clicks
        this.setupVariantImageClicks();

        // Listen to media gallery slide changes to sync with Dawn
        this.setupMediaGallerySync();
      }

      ensureDesktopSlider() {
        // Ensure slider works on desktop by making sure slider-component is properly initialized
        const sliderComponent = this.querySelector('slider-component[id^="GalleryViewer"]');
        if (sliderComponent) {
          // Ensure the slider component has proper styling for desktop
          // Dawn's slider-component should handle this, but we ensure it's not blocked
          const mediaList = sliderComponent.querySelector('.product__media-list');
          if (mediaList) {
            // Make sure pointer events work for modal-opener
            const modalOpeners = mediaList.querySelectorAll('.product__modal-opener');
            modalOpeners.forEach(opener => {
              opener.style.pointerEvents = 'auto';
              opener.style.zIndex = '1';
            });
          }
        }
      }

      buildVariantMediaMap() {
        // Get all variant inputs with media IDs
        const variantInputs = document.querySelectorAll('input[data-variant-media-id]');
        variantInputs.forEach(input => {
          const mediaId = input.dataset.variantMediaId;
          if (mediaId) {
            this.variantMediaMap.set(mediaId, input);
          }
        });
      }

      setupVariantListeners() {
        // Use Dawn's pub/sub system to listen to variant changes
        // Dawn's product-info.js already calls setActiveMedia, but we ensure variant image clicks work
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          // Subscribe to option value selection changes (Dawn's native event)
          this.unsubscribeVariantChanges = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            (event) => {
              const { target, selectedOptionValues } = event.data || {};
              
              // Check if the changed input has a variant media ID
              if (target && target.dataset?.variantMediaId) {
                const mediaId = target.dataset.variantMediaId;
                // Wait a bit for variant selection to process
                setTimeout(() => {
                  this.switchToVariantImage(mediaId);
                }, 150);
              }
            }
          );

          // Also subscribe to variant change events
          this.unsubscribeVariantChange = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              const variant = event.data?.variant;
              if (variant?.featured_media?.id) {
                // Dawn's product-info.js should handle this, but we ensure it works
                this.ensureVariantImageActive(variant.featured_media.id);
              }
            }
          );
        } else {
          // Fallback: listen directly to option changes
          document.addEventListener('change', (e) => {
            const input = e.target;
            if ((input.type === 'radio' || input.type === 'checkbox') && input.dataset.variantMediaId) {
              const mediaId = input.dataset.variantMediaId;
              setTimeout(() => {
                this.switchToVariantImage(mediaId);
              }, 150);
            }
          });
        }
      }


      setupVariantImageClicks() {
        // Listen for clicks on variant option images/swatches
        document.addEventListener('click', (e) => {
          const label = e.target.closest('.variant-option-label, .variant-image-label, label[for]');
          if (!label) return;

          // Find the associated input
          let input = null;
          if (label.tagName === 'LABEL') {
            const labelFor = label.getAttribute('for');
            if (labelFor) {
              input = document.getElementById(labelFor);
            } else {
              input = label.previousElementSibling;
            }
          } else {
            // Check if clicked element is inside a label
            const parentLabel = e.target.closest('label');
            if (parentLabel) {
              const labelFor = parentLabel.getAttribute('for');
              input = labelFor ? document.getElementById(labelFor) : parentLabel.previousElementSibling;
            }
          }

          // Check if it's a variant input with media ID
          if (input && (input.type === 'radio' || input.type === 'checkbox') && input.dataset.variantMediaId) {
            const mediaId = input.dataset.variantMediaId;
            // Wait a bit for variant selection to process
            setTimeout(() => {
              this.switchToVariantImage(mediaId);
            }, 100);
          }
        });
      }

      setupMediaGallerySync() {
        // Listen to Dawn's media gallery slide changes
        if (this.mediaGallery && this.mediaGallery.elements?.viewer) {
          this.mediaGallery.elements.viewer.addEventListener('slideChanged', (event) => {
            // Sync with Dawn's slider if needed
            // Dawn handles this automatically, but we can add custom logic here
          });
        }
      }

      switchToVariantImage(mediaId) {
        if (!mediaId || !this.mediaGallery) return;

        // Get section ID
        const productInfo = document.querySelector('product-info');
        const sectionId = productInfo?.dataset?.section || this.sectionId;

        if (!sectionId) {
          console.warn('ProductGallerySlider: Section ID not found');
          return;
        }

        // Format: sectionId-mediaId (Dawn's format)
        const fullMediaId = `${sectionId}-${mediaId}`;

        // Use Dawn's native setActiveMedia method
        if (typeof this.mediaGallery.setActiveMedia === 'function') {
          // prepend: true moves the media to the front and makes it active
          this.mediaGallery.setActiveMedia(fullMediaId, true);
        } else {
          console.warn('ProductGallerySlider: setActiveMedia method not found on media-gallery');
        }
      }

      ensureVariantImageActive(mediaId) {
        // Ensure variant image is active (Dawn's product-info.js should handle this,
        // but we ensure it works as a fallback)
        if (!mediaId) return;

        const productInfo = document.querySelector('product-info');
        const sectionId = productInfo?.dataset?.section || this.sectionId;

        if (!sectionId) return;

        const fullMediaId = `${sectionId}-${mediaId}`;
        
        // Check if Dawn already activated it
        const activeMedia = this.mediaGallery?.elements?.viewer?.querySelector('[data-media-id].is-active');
        if (activeMedia && activeMedia.dataset.mediaId === fullMediaId) {
          // Already active, no need to switch
          return;
        }

        // Switch if not already active
        this.switchToVariantImage(mediaId);
      }

      disconnectedCallback() {
        // Cleanup subscriptions
        if (this.unsubscribeVariantChanges) {
          this.unsubscribeVariantChanges();
        }
        if (this.unsubscribeVariantChange) {
          this.unsubscribeVariantChange();
        }
      }
    }
  );
}

// Initialize variant image click handler for better integration
class VariantImageClickHandler {
  constructor() {
    this.setup();
  }

  setup() {
    // Listen for clicks on variant option images
    document.addEventListener('click', (e) => {
      // Check if clicked on variant image swatch
      const variantImage = e.target.closest('.variant-image-swatch, .variant-image-label');
      if (!variantImage) return;

      // Find the associated input
      const label = variantImage.closest('label');
      if (!label) return;

      const labelFor = label.getAttribute('for');
      const input = labelFor ? document.getElementById(labelFor) : label.previousElementSibling;

      if (!input || !input.dataset.variantMediaId) return;

      const mediaId = input.dataset.variantMediaId;
      const productGallery = document.querySelector('product-gallery-slider');
      
      if (productGallery && typeof productGallery.switchToVariantImage === 'function') {
        // Wait for variant selection to process
        setTimeout(() => {
          productGallery.switchToVariantImage(mediaId);
        }, 100);
      } else {
        // Fallback: use media gallery directly
        const productInfo = document.querySelector('product-info');
        const mediaGallery = document.querySelector('media-gallery');
        
        if (productInfo && mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          const sectionId = productInfo.dataset.section;
          if (sectionId) {
            setTimeout(() => {
              mediaGallery.setActiveMedia(`${sectionId}-${mediaId}`, true);
            }, 100);
          }
        }
      }
    });
  }
}

// Initialize variant image click handler
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantImageClickHandler();
  });
} else {
  new VariantImageClickHandler();
}

